// ---------------------------------------------------------------------------
// Voice dictation command parsing — shared by the Note body editor (TipTap)
// and the Task detail Comments box (plain textarea).
//
// The Web Speech API returns plain, unpunctuated text. To let users add
// punctuation and breaks hands-free, we recognise spoken command phrases
// ("full stop", "new line", "comma", …) and convert them into real
// characters / editor actions. Everything else is treated as literal text.
// ---------------------------------------------------------------------------

export const SPEECH_SUPPORTED =
  typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

// Spoken phrase (lowercase) -> operation.
//   punct  : attaches to the previous word with no leading space
//   hyphen : "-" that joins the words either side (well-being)
//   dash   : spaced em dash ( — )
//   quote  : typographic double quotes ("open" / "close")
//   newline / paragraph / bullet : structural breaks
//   delete_word : removes the previous word ("delete that")
const PHRASE_MAP = {
  'new line': { op: 'newline' },
  newline: { op: 'newline' },
  'new paragraph': { op: 'paragraph' },
  'new bullet': { op: 'bullet' },
  'bullet point': { op: 'bullet' },
  'full stop': { op: 'punct', value: '.' },
  period: { op: 'punct', value: '.' },
  comma: { op: 'punct', value: ',' },
  'question mark': { op: 'punct', value: '?' },
  'exclamation mark': { op: 'punct', value: '!' },
  'exclamation point': { op: 'punct', value: '!' },
  colon: { op: 'punct', value: ':' },
  semicolon: { op: 'punct', value: ';' },
  'semi colon': { op: 'punct', value: ';' },
  hyphen: { op: 'hyphen' },
  dash: { op: 'dash' },
  'open quote': { op: 'quote', value: 'open' },
  'open quotes': { op: 'quote', value: 'open' },
  'close quote': { op: 'quote', value: 'close' },
  'close quotes': { op: 'quote', value: 'close' },
  'delete that': { op: 'delete_word' },
  'delete word': { op: 'delete_word' },
};

const MAX_PHRASE_WORDS = 2;

// Grouped list for the on-screen help hint.
export const DICTATION_COMMAND_GROUPS = [
  {
    title: 'Punctuation',
    items: [
      { say: 'full stop', gives: '.' },
      { say: 'comma', gives: ',' },
      { say: 'question mark', gives: '?' },
      { say: 'exclamation mark', gives: '!' },
      { say: 'colon', gives: ':' },
      { say: 'semicolon', gives: ';' },
      { say: 'hyphen', gives: '-' },
      { say: 'dash', gives: '—' },
    ],
  },
  {
    title: 'Quotes',
    items: [
      { say: 'open quote', gives: '\u201C' },
      { say: 'close quote', gives: '\u201D' },
    ],
  },
  {
    title: 'Breaks',
    items: [
      { say: 'new line', gives: '\u21B5' },
      { say: 'new paragraph', gives: '\u00B6' },
      { say: 'new bullet', gives: '\u2022' },
    ],
  },
  {
    title: 'Editing',
    items: [{ say: 'delete that', gives: 'removes last word' }],
  },
];

// Convert a finalised speech transcript into an ordered list of ops.
// Each op is either { op:'text', value } or a command op.
export function tokenizeDictation(raw) {
  const words = String(raw || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const ops = [];
  let buf = [];
  const flush = () => {
    if (buf.length) {
      ops.push({ op: 'text', value: buf.join(' ') });
      buf = [];
    }
  };
  let i = 0;
  while (i < words.length) {
    let matched = null;
    for (let n = Math.min(MAX_PHRASE_WORDS, words.length - i); n >= 1; n -= 1) {
      // Chrome may auto-attach punctuation; strip it before matching.
      const phrase = words
        .slice(i, i + n)
        .join(' ')
        .toLowerCase()
        .replace(/[.,!?;:]+$/, '');
      if (PHRASE_MAP[phrase]) {
        matched = { def: PHRASE_MAP[phrase], n };
        break;
      }
    }
    if (matched) {
      flush();
      ops.push({ ...matched.def });
      i += matched.n;
    } else {
      buf.push(words[i]);
      i += 1;
    }
  }
  flush();
  return ops;
}

// True when the next text run should be capitalised, given the text before it.
const shouldCapitalise = (before) => {
  const t = before.replace(/\s+$/, '');
  if (t === '') return true;
  return /[.!?]["'\u201D\u2019)\]]*$/.test(t);
};

const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// ---------------------------------------------------------------------------
// Pure applier for a plain <textarea>. Returns the new value + caret index.
// ---------------------------------------------------------------------------
export function applyOpsToText(prevText, selStart, selEnd, ops) {
  let before = String(prevText).slice(0, selStart);
  const after = String(prevText).slice(selEnd);
  let joinNext = false; // suppress the leading space before the next text run
  let forceCap = false; // force-capitalise the next text run (after a break)

  const openBefore = (s) => /[([\u201C"'\-]$/.test(s);

  for (const seg of ops) {
    switch (seg.op) {
      case 'text': {
        let chunk = seg.value;
        if (forceCap || shouldCapitalise(before)) chunk = capFirst(chunk);
        const needSpace =
          !!before && !/\s$/.test(before) && !joinNext && !openBefore(before);
        before += (needSpace ? ' ' : '') + chunk;
        joinNext = false;
        forceCap = false;
        break;
      }
      case 'punct': {
        before = before.replace(/\s+$/, '') + seg.value;
        joinNext = false;
        forceCap = false;
        break;
      }
      case 'hyphen': {
        before = before.replace(/\s+$/, '') + '-';
        joinNext = true;
        break;
      }
      case 'dash': {
        before = /\s$/.test(before) ? `${before}\u2014 ` : `${before} \u2014 `;
        joinNext = false;
        break;
      }
      case 'quote': {
        if (seg.value === 'open') {
          const needSpace = !!before && !/\s$/.test(before) && !openBefore(before);
          before += (needSpace ? ' ' : '') + '\u201C';
          joinNext = true;
        } else {
          before = before.replace(/\s+$/, '') + '\u201D';
          joinNext = false;
        }
        break;
      }
      case 'newline': {
        before = before.replace(/[ \t]+$/, '') + '\n';
        joinNext = false;
        forceCap = true;
        break;
      }
      case 'paragraph': {
        before = before.replace(/[ \t]+$/, '') + '\n\n';
        joinNext = false;
        forceCap = true;
        break;
      }
      case 'bullet': {
        before = before.replace(/[ \t]+$/, '');
        if (before && !before.endsWith('\n')) before += '\n';
        before += '\u2022 ';
        joinNext = true;
        forceCap = true;
        break;
      }
      case 'delete_word': {
        before = before.replace(/\s+$/, '').replace(/\S+$/, '').replace(/\s+$/, '');
        joinNext = false;
        break;
      }
      default:
        break;
    }
  }
  return { text: before + after, caret: before.length };
}

// ---------------------------------------------------------------------------
// Imperative applier for a TipTap editor instance.
// ---------------------------------------------------------------------------
export function applyOpsToEditor(editor, ops) {
  if (!editor) return;

  const beforeChars = () => {
    try {
      const { from } = editor.state.selection;
      if (from <= 1) return '';
      return editor.state.doc.textBetween(Math.max(0, from - 2), from, '\n', ' ');
    } catch {
      return '';
    }
  };
  const docEmpty = () => !editor.state.doc.textContent.trim();
  const openBefore = (s) => /[([\u201C"'\-]$/.test(s);
  const insert = (str) => editor.chain().focus().insertContent(str).run();

  let joinNext = false;
  let forceCap = false;

  for (const seg of ops) {
    const before = beforeChars();
    switch (seg.op) {
      case 'text': {
        let chunk = seg.value;
        if (forceCap || docEmpty() || shouldCapitalise(before)) chunk = capFirst(chunk);
        const needSpace = !!before && !/\s$/.test(before) && !joinNext && !openBefore(before);
        insert((needSpace ? ' ' : '') + chunk);
        joinNext = false;
        forceCap = false;
        break;
      }
      case 'punct': {
        insert(seg.value);
        joinNext = false;
        forceCap = false;
        break;
      }
      case 'hyphen': {
        insert('-');
        joinNext = true;
        break;
      }
      case 'dash': {
        insert((/\s$/.test(before) ? '' : ' ') + '\u2014 ');
        joinNext = false;
        break;
      }
      case 'quote': {
        if (seg.value === 'open') {
          const needSpace = !!before && !/\s$/.test(before) && !openBefore(before);
          insert((needSpace ? ' ' : '') + '\u201C');
          joinNext = true;
        } else {
          insert('\u201D');
          joinNext = false;
        }
        break;
      }
      case 'newline': {
        editor.chain().focus().setHardBreak().run();
        joinNext = false;
        forceCap = true;
        break;
      }
      case 'paragraph': {
        editor.chain().focus().splitBlock().run();
        joinNext = false;
        forceCap = true;
        break;
      }
      case 'bullet': {
        if (editor.isActive('bulletList')) editor.chain().focus().splitListItem('listItem').run();
        else editor.chain().focus().toggleBulletList().run();
        joinNext = false;
        forceCap = true;
        break;
      }
      case 'delete_word': {
        try {
          const { from } = editor.state.selection;
          const start = Math.max(1, from - 80);
          const textBefore = editor.state.doc.textBetween(start, from, '\n', ' ');
          const trimmed = textBefore.replace(/\s+$/, '');
          const word = trimmed.match(/\S+$/);
          if (word) {
            const delLen = textBefore.length - trimmed.length + word[0].length;
            editor.chain().focus().deleteRange({ from: from - delLen, to: from }).run();
          }
        } catch {
          /* noop */
        }
        joinNext = false;
        break;
      }
      default:
        break;
    }
  }
}
